import React, { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import BlogContent from "../components/Blog/BlogContent";
import LoadingSpinner from "../components/LoadingSpinner";
import { buildBlogPostSeo } from "../config/seo";
import { useFetchBlog } from "../hooks/useFetchBlogs";
import "./blog.css";

const formatArticleDate = (dateStr) => {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date
    .toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    .toUpperCase();
};

const formatReadTime = (readTime) => {
  if (readTime === undefined || readTime === null || readTime === "") {
    return null;
  }
  const raw = String(readTime).trim();
  if (!raw) return null;
  if (/min\s*read/i.test(raw)) {
    return raw.toUpperCase();
  }
  const num = Number(raw);
  if (!Number.isNaN(num) && num > 0) {
    return `${num} MIN READ`;
  }
  return raw.toUpperCase();
};

const BlogDetails = () => {
  const { slug } = useParams();
  const {
    data: post,
    isLoading,
    isError,
  } = useFetchBlog(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const blogSeo = useMemo(() => {
    if (!post) return null;
    return buildBlogPostSeo(post, slug);
  }, [post, slug]);

  if (isLoading) {
    return (
      <Layout seo={null}>
        <div className="azzir-blog azzir-blog--detail">
          <LoadingSpinner className="spinner-container--page" />
        </div>
      </Layout>
    );
  }

  if (isError || !post) {
    return <Navigate to="/blog" replace />;
  }

  const readTimeLabel = formatReadTime(post.readTime);
  const heroSrc = post.featuredImage || "";

  return (
    <Layout seo={blogSeo ?? undefined}>
      <div className="azzir-blog azzir-blog--detail">
        <article className="azzir-blog-article-page">
          <div className="container">
            <nav className="azzir-blog-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/blog">Blogs</Link>
              <span>/</span>
              <span aria-current="page">{post.title}</span>
            </nav>

            <div className="azzir-blog-article-page__inner">
              {heroSrc ? (
                <figure className="azzir-blog-article-page__hero">
                  <img src={heroSrc} alt={post.title} />
                </figure>
              ) : null}

              <header className="azzir-blog-article-page__header">
                <h1>{post.title}</h1>

                {post.author ? (
                  <p className="azzir-blog-article-page__author-name">
                    {post.author}
                  </p>
                ) : null}

                <p className="azzir-blog-article-page__meta">
                  {post.publishDate ? (
                    <time dateTime={post.publishDate}>
                      {formatArticleDate(post.publishDate)}
                    </time>
                  ) : null}
                  {post.publishDate && readTimeLabel ? (
                    <span
                      className="azzir-blog-article-page__sep"
                      aria-hidden="true"
                    >
                      |
                    </span>
                  ) : null}
                  {readTimeLabel ? <span>{readTimeLabel}</span> : null}
                </p>
              </header>

              <div className="azzir-blog-article-page__content">
                <BlogContent content={post.content} />
              </div>
            </div>
          </div>
        </article>
      </div>
    </Layout>
  );
};

export default BlogDetails;
